import {
  AbsoluteCenter,
  Box,
  FormControl,
  FormLabel,
  HStack,
  Image,
  ModalCloseButton,
  PinInput,
  Text,
} from '@chakra-ui/react';
import { useState } from 'react';

import AcceptApprovalImg from '../../../../../assets/images/accept-approval.svg';
import {
  AnimatedLogo,
  CustomModalActionPrimaryButton,
  CustomModalFooter,
  CustomPinInputField,
  PrimaryButton,
  SecondaryButton,
  StatusBox,
} from '../../../../../components/custom';
import SuccessButton from '../../../../../components/custom/CustomButtons/SuccessButton';
import { useAppDispatch, useAppSelector } from '../../../../../store/hooks';
import {
  bulkApproveTransactionAsync,
  resetTransactionBulkApprove,
} from '../../../../../store/slices/transactionMandate/bulkApproveTransaction';
import CreateTransactionPin from '../../../../TransactionPin/CreateTransactionPin';

interface ApproveTransactionMandateProps {
  selectedTransactions: (string | number)[];
  handleResetSelectedTransactions: () => void;
}

type TApproveScreens = 'selection' | 'pin';

export default function BulkApproveTransactions({
  selectedTransactions,
  handleResetSelectedTransactions,
}: ApproveTransactionMandateProps) {
  const [approveScreen, setApproveScreen] =
    useState<TApproveScreens>('selection');

  const handleApprove = () => {
    setApproveScreen('pin');
  };

  const [pin, setPin] = useState('');

  const dispatch = useAppDispatch();

  const { status, value } = useAppSelector(
    (state) => state.bulkApproveTransaction
  );

  const handleSubmitApproveTransaction = () => {
    dispatch(
      bulkApproveTransactionAsync({
        pin,
        approvalIds: selectedTransactions.map((transaction) =>
          transaction.toString()
        ),
      })
    );
  };

  const [userHasPin, setUserHasPin] = useState(
    localStorage.getItem('userHasPin')
  );

  if (!userHasPin) {
    return <CreateTransactionPin setUserHasPin={setUserHasPin} />;
  }

  if (approveScreen === 'pin') {
    if (status === 'loading') {
      return (
        <Box minH="330px">
          <AbsoluteCenter>
            <AnimatedLogo />
          </AbsoluteCenter>
        </Box>
      );
    }

    if (status === 'success') {
      handleResetSelectedTransactions();

      return (
        <>
          <Box minH="330px" mb="82px">
            <AbsoluteCenter axis="vertical" top="45%" left="0" right="0">
              <StatusBox
                variant="success"
                title="Transactions Approved Successfully."
              >
                {value?.message ??
                  'The transactions have been successfully approved.'}
              </StatusBox>
            </AbsoluteCenter>
          </Box>
          <CustomModalFooter>
            <PrimaryButton
              as={ModalCloseButton}
              w="full"
              top="initial"
              right="initial"
            >
              Dismiss
            </PrimaryButton>
          </CustomModalFooter>
        </>
      );
    }

    if (status === 'failed') {
      return (
        <>
          <Box minH="330px" mb="82px">
            <AbsoluteCenter axis="vertical" top="45%" left="0" right="0">
              <StatusBox variant="error" title="Failed to approve transaction.">
                {value?.message ??
                  'An error occurred while approving the transactions. Please try again.'}
              </StatusBox>
            </AbsoluteCenter>
          </Box>
          <CustomModalFooter>
            <PrimaryButton
              onClick={() => dispatch(resetTransactionBulkApprove())}
              w="full"
            >
              Retry
            </PrimaryButton>
          </CustomModalFooter>
        </>
      );
    }

    return (
      <Box>
        <Box pt={10} pb={4}>
          <Text
            color="#1a1c1f"
            fontSize="1.5rem"
            lineHeight="133.33%"
            mb={3}
            textAlign="center"
          >
            PIN
          </Text>
          <Text
            fontSize="16px"
            color="gray.200"
            lineHeight="143.75%"
            textAlign="center"
            mb={3}
          >
            Enter your 4-digit PIN to reject this transaction
          </Text>
        </Box>

        <FormControl id="pin" mb="114px">
          <FormLabel fontSize=".875rem">Enter PIN</FormLabel>
          <HStack>
            <PinInput mask value={pin} onChange={(value) => setPin(value)}>
              <CustomPinInputField pinInputVariant="transactionPin" />
              <CustomPinInputField pinInputVariant="transactionPin" />
              <CustomPinInputField pinInputVariant="transactionPin" />
              <CustomPinInputField pinInputVariant="transactionPin" />
            </PinInput>
          </HStack>
        </FormControl>

        <CustomModalFooter>
          <CustomModalActionPrimaryButton
            isDisabled={!pin}
            onClick={handleSubmitApproveTransaction}
          >
            Approve Transactions
          </CustomModalActionPrimaryButton>
        </CustomModalFooter>
      </Box>
    );
  }

  return (
    <Box>
      <Box pt={10} pb={4}>
        <Image
          src={AcceptApprovalImg}
          alt=""
          loading="lazy"
          boxSize={200}
          mx="auto"
          mb={4}
        />
        <Text fontSize="24px" lineHeight="133%" textAlign="center" mb={3}>
          Approve {selectedTransactions.length}{' '}
          {selectedTransactions.length === 1 ? 'Transaction' : 'Transactions'}
        </Text>
        <Text color="gray.200" textAlign="center" letterSpacing="-0.12px">
          Are you sure you want to approve these transactions? This action is
          irreversible.
        </Text>
      </Box>
      <CustomModalFooter>
        <HStack spacing={4}>
          <SuccessButton w="full" onClick={handleApprove}>
            Yes, Approve
          </SuccessButton>
          <SecondaryButton
            w="full"
            as={ModalCloseButton}
            top="initial" // reset ModalCloseButton styles
            right="initial"
            flexShrink="initial"
          >
            Cancel, Keep
          </SecondaryButton>
        </HStack>
      </CustomModalFooter>
    </Box>
  );
}
